import { Injectable, inject } from '@angular/core';
import { ReplaySubject, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';

import type { AppUserRole } from '@aveespa/common/models/auth/app-roles.interface';
import { TenantContextService } from 'app/@core/services/utility/tenant-context.service';
import { UserService } from 'app/@core/user/user.service';
import { mainNavigation } from './navigation.data';
import {
    AppNavigationItem,
    APP_PLATFORM_TYPES,
    AppPlatform,
    Navigation,
} from './navigation.types';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private readonly _tenantCtx  = inject(TenantContextService);
    private readonly _userService = inject(UserService);

    private _navigation$ = new ReplaySubject<Navigation>(1);

    private _deviceType: AppPlatform = APP_PLATFORM_TYPES.Web;
    /** Real "installed Capacitor app" signal — independent of `_deviceType`, which is a screen-width proxy (see dense.component.ts) and also matches phone browsers. */
    private readonly _isNativeApp = Capacitor.isNativePlatform();
    private _currentRole: AppUserRole | null = null;
    /** Raw stored value — combined with role at read time in _hasOfficeAccess(), not cached, so it's never stale relative to whichever of the two subscriptions below fired last. */
    private _storedHasOfficeAccess = false;

    constructor() {
        // Rebuild navigation whenever the user's role changes.
        this._tenantCtx.role$.subscribe((role) => {
            this._currentRole = role;
            this._rebuildNavigation();
        });

        // Rebuild whenever hasOfficeAccess changes (field_staff may or may not have it).
        this._userService.appUser$.subscribe((user) => {
            this._storedHasOfficeAccess = user?.hasOfficeAccess ?? false;
            this._rebuildNavigation();
        });

        // Build initial navigation before claims resolve (shows platform-filtered
        // items that have no role restriction).
        this._rebuildNavigation();
    }

    /**
     * office_staff/system_admin/support are documented as "always true,
     * enforced by CF" (see AppUser.hasOfficeAccess) — but that enforcement
     * only runs at create/update time, so a record written before that rule
     * existed (or edited outside the normal flow) can still carry a stale
     * `false`. Rather than rely on that write-time guarantee holding for
     * every record forever, treat it as always true for those roles here
     * too — the nav's `requiresOfficeAccess` filter should never be the
     * reason one of these roles can't see Office Queue when the route
     * itself has no such restriction. Computed fresh (not cached) so it's
     * never stale relative to whichever of the two constructor subscriptions
     * fired last.
     */
    private _hasOfficeAccess(): boolean {
        const alwaysHasOfficeAccess = this._currentRole === 'system_admin'
            || this._currentRole === 'office_staff'
            || this._currentRole === 'support';
        return alwaysHasOfficeAccess || this._storedHasOfficeAccess;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public accessors
    // -----------------------------------------------------------------------------------------------------

    get navigation$(): Observable<Navigation> {
        return this._navigation$.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Called by layout when screen size changes.
     */
    updateDeviceType(deviceType: AppPlatform): void {
        if (this._deviceType === deviceType) {
            return;
        }

        this._deviceType = deviceType;
        this._rebuildNavigation();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Internal helpers
    // -----------------------------------------------------------------------------------------------------

    private _rebuildNavigation(): void {
        const filtered = this._filterNavigation(mainNavigation, this._deviceType);

        const navigation: Navigation = {
            compact   : filtered,
            default   : filtered,
            futuristic: filtered,
            horizontal: filtered,
        };

        this._navigation$.next(navigation);
    }

    /**
     * Recursively filters navigation items by platform and role.
     *
     * An item is included when:
     *   - Its `meta.platform` matches the current device type (or is 'both').
     *   - Its `meta.roles` array is absent OR contains the current user role.
     *
     * Groups with no visible children after filtering are dropped entirely.
     */
    private _filterNavigation(
        items: AppNavigationItem[],
        deviceType: AppPlatform,
    ): AppNavigationItem[] {
        return items
            .map((item) => {
                // Installed-app filter — hides web-only sections regardless of screen width
                if (this._isNativeApp && item.meta?.hiddenInApp) {
                    return null;
                }

                // Platform filter
                const visibility = item.meta?.platform ?? APP_PLATFORM_TYPES.Both;
                if (
                    visibility !== APP_PLATFORM_TYPES.Both &&
                    visibility !== deviceType
                ) {
                    return null;
                }

                // Role filter — if meta.roles is defined, the current role must be in it
                const allowedRoles = item.meta?.roles;
                if (allowedRoles && !allowedRoles.includes(this._currentRole as string)) {
                    return null;
                }

                // Office-access filter — hide items that require hasOfficeAccess when the user doesn't have it
                if (item.meta?.requiresOfficeAccess && !this._hasOfficeAccess()) {
                    return null;
                }

                // Recurse into children
                if (item.children?.length) {
                    const children = this._filterNavigation(
                        item.children as AppNavigationItem[],
                        deviceType,
                    );
                    // Drop groups that become empty after filtering
                    if (item.type === 'group' && children.length === 0) {
                        return null;
                    }
                    return { ...item, children };
                }

                return item;
            })
            .filter((item): item is AppNavigationItem => item !== null);
    }
}
